import clsx from "clsx";
import { observe } from "mobx";
import { inject, observer } from "mobx-react";
import Image from "next/image";
import { CSSProperties, RefObject, useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Store } from "stores/store";
import { motion, useTransform, useMotionValue } from "framer-motion";
import ExportedImage from "next-image-export-optimizer";
import ChatBubble from "../web/chat/ChatBubble";
import { getImageUrlFromGeneralImage } from "utils/imageHelpers";

interface ChatMessageTextProps {
    characterImageSrc: string;
    characterImageAlt: string;
    title: string;
    text: string;
    isMainChatMessage: boolean;
    store?: Store;
    isUser: boolean;
    disableTitle: boolean;
    timeToAppear?: Date;
    switchBackground?: string;
    triggerEffect?: string;
    scrollRef: RefObject<HTMLDivElement>;
    typingDuration?: number;
    emoji?: string;
}

const ChatMessageText: React.FunctionComponent<ChatMessageTextProps> = (props) => {
    if (!props.store) {
        throw Error("Store was not setup correctly");
    }

    const { chatMessageEnlarged, updateChatBackground, updateChatEffect, chatbotLocalized } = props.store;

    const delay = props.timeToAppear ? Math.max(props.timeToAppear.getTime() - new Date().getTime(), 0) / 1000 : 0;
    const typingDuration = props.typingDuration ?? 0;
    const STATUS_TAG_REGEX = /\[STATUS\]([\s\S]*?)\[\/STATUS\]/gi;

    let rawMessageText = props.text;
    const extractedStatusParts: string[] = [];

    try {
        const parsedPayload = JSON.parse(props.text);
        if (parsedPayload && typeof parsedPayload === "object") {
            if (typeof parsedPayload.reply === "string") {
                rawMessageText = parsedPayload.reply;
            }
            if (typeof parsedPayload.status === "string" && parsedPayload.status.trim().length > 0) {
                extractedStatusParts.push(parsedPayload.status.trim());
            }
        }
    } catch {
        // ignore non-JSON payloads
    }

    const messageText = rawMessageText
        .replace(STATUS_TAG_REGEX, (_, statusText: string) => {
            const cleanStatus = statusText.trim();
            if (cleanStatus.length > 0) {
                extractedStatusParts.push(cleanStatus);
            }
            return "";
        })
        .trim();
    const statusText = extractedStatusParts.join(" ").trim();
    const hasMessageText = messageText.length > 0;
    const hasStatusText = statusText.length > 0;
    const hasContent = hasMessageText || hasStatusText;
    const writingDuration = hasMessageText ? typingDuration : 0;



    const chatMessageRef = useRef<HTMLDivElement>(null);

    const yProgress = useMotionValue(0);
    const parentOffsetHeight = props.scrollRef.current?.offsetHeight ?? 0;

    const animate = () => {
        if (chatMessageRef.current) {
            const offsetBottom =
                parentOffsetHeight - chatMessageRef.current?.offsetTop - chatMessageRef.current?.offsetHeight;
            yProgress.set(offsetBottom + 2 * (props.scrollRef.current?.scrollTop ?? 0));
        }
        requestAnimationFrame(animate);
    };

    useEffect(() => {
        setTimeout(() => {
            animate();
        }, delay);
    }, []);

    const isOnline: boolean = process.env.NEXT_PUBLIC_IS_ONLINE === "true";

    const yProgressOpacity = useTransform(
        yProgress,
        [parentOffsetHeight / 2, parentOffsetHeight],
        [1, -3],
    );

    const variantsContainer = {
        hidden: { height: 0, opacity: 0 },
        shown: {
            height: "auto",
            width: "auto",
            opacity: 1,
            transition: { duration: 0.3, delay: delay, ease: "circInOut" },
        },
        enlarged: {
            opacity: 0,
            height: 0,
            y: 800,
            width: 0,
            transition: { duration: 0.3, delay: 0 },
        },
    };

    const variantsWriting = {
        hidden: { opacity: 1, transition: { duration: 0 } },
        shown: {
            opacity: 0,
            transition: { duration: 0.1, delay: delay + writingDuration, ease: "circInOut" },
        },
    };

    const variantsText = {
        hidden: { opacity: 0, height: isOnline ? "14px" : "67px", width: isOnline ? "58px" : "255px" },
        shown: {
            opacity: 1,
            height: "auto",
            width: "auto",
            transition: { duration: 0, delay: delay + writingDuration },
        },
    };

    const variantsStatus = {
        hidden: {
            opacity: 0,
            height: 0,
            y: 8,
        },
        shown: {
            opacity: 1,
            height: "auto",
            y: 0,
            transition: { duration: 0.2, delay, ease: "circInOut" },
        },
    };

    const triggerEffects = () => {
        if (props.switchBackground) updateChatBackground(props.switchBackground);
        if (props.triggerEffect) updateChatEffect(props.triggerEffect);
    };

    return (
        <motion.div
            variants={variantsContainer}
            initial="hidden"
            animate={chatMessageEnlarged ? "enlarged" : "shown"}
            ref={chatMessageRef}
            style={{ scale: yProgressOpacity }}
        >
            <motion.div style={{ scale: yProgressOpacity, opacity: yProgressOpacity }}>
                {!hasContent ? null : (
                    <div className="w-full">
                        {hasStatusText && (
                            <motion.div
                                variants={variantsStatus}
                                initial="hidden"
                                animate="shown"
                                className="py-1 w-full"
                            >
                                <div className="font-frutigerBold text-xs text-center italic">
                                    {statusText}
                                </div>
                            </motion.div>
                        )}

                        {hasMessageText && (
                            <ChatBubble
                                title={!props.disableTitle ? (props.isUser ? "HEUTE-ICH" : "ZUKUNFTS-ICH") : ""}
                                alignRight={props.isUser}
                                alignLeft={!props.isUser}
                            >
                                <motion.div
                                    variants={variantsWriting}
                                    initial={writingDuration > 0 ? "hidden" : "shown"}
                                    animate={"shown"}
                                    className="absolute"
                                    onAnimationComplete={() => {
                                        triggerEffects();
                                    }}
                                >
                                    <ExportedImage
                                        src={
                                            chatbotLocalized?.chatWritingGif !== undefined &&
                                                chatbotLocalized?.chatWritingGif !== null
                                                ? getImageUrlFromGeneralImage(chatbotLocalized?.chatWritingGif)
                                                : "No image provided"
                                        }
                                        alt={"chat"}
                                        className="relative"
                                        height={isOnline ? 14 : 58}
                                        width={isOnline ? 62 : 255}
                                    />
                                </motion.div>
                                <motion.div
                                    variants={variantsText}
                                    initial={writingDuration > 0 ? "hidden" : "shown"}
                                    animate={"shown"}
                                >
                                    <ReactMarkdown>{messageText + " " + props.emoji}</ReactMarkdown>
                                </motion.div>
                            </ChatBubble>
                        )}
                    </div>
                )}
            </motion.div>
        </motion.div>
    );
};

export default inject("store")(observer(ChatMessageText));
